import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback, Fragment, ReactNode } from "react";

import { Box, Button, Chip, FormControl, Grid2, IconButton, InputLabel, Link, Menu, MenuItem, Select, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PageTitle from "../components/PageTitle";
import { api, Match, MatchStatus, Player, VersionChannel } from "../../shared/api/client";
import useGameAndVersion from "../../shared/hooks/useGameAndVersion";
import { InternalLink } from "../../shared/components/InternalLink";
import BoxCard from "../../shared/components/BoxCard";
import { useAuth } from "../../shared/auth/AuthContext";
import { useMatchSubscription } from "../../shared/hooks/useMatchSubscription";
import {
  AI_DIFFICULTY_LABEL,
  AI_DIFFICULTY_LEVELS,
  AiDifficultyLevel,
  aiDifficultyLevelFor,
  isAiPlayer,
} from "../../shared/util/aiSeats";

const AI_DIFFICULTY_BLURB: Record<AiDifficultyLevel, string> = {
  low: "Casual",
  medium: "Solid",
  high: "Sharp",
};

function InfoLabel({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

function InfoValue({ children }: { children: ReactNode }) {
  return <Typography variant="body2" component="div" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{children}</Typography>;
}

/** A bot's strength as a chip; when manageable, clicking it opens a menu to change it. */
function BotStrengthChip({
  level,
  canManage,
  busy,
  onChange,
}: {
  level: AiDifficultyLevel;
  canManage: boolean;
  busy: boolean;
  onChange: (level: AiDifficultyLevel) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = AI_DIFFICULTY_LABEL[level];

  if (!canManage) {
    return <Chip label={label} size="small" variant="outlined" />;
  }

  return (
    <>
      <Chip
        label={label}
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={(e) => setAnchor(e.currentTarget)}
        onDelete={(e) => setAnchor(e.currentTarget)}
        deleteIcon={<ArrowDropDownIcon />}
      />
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {AI_DIFFICULTY_LEVELS.map((l) => (
          <MenuItem
            key={l}
            selected={l === level}
            onClick={() => {
              setAnchor(null);
              if (l !== level) onChange(l);
            }}
          >
            {AI_DIFFICULTY_LABEL[l]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function PlayerInfo({
  player,
  canManage,
  busy,
  onSetDifficulty,
  onRemove,
}: {
  player: Player;
  canManage: boolean;
  busy: boolean;
  onSetDifficulty: (level: AiDifficultyLevel) => void;
  onRemove: () => void;
}) {
  // Same body2 size as the match info values; icons in the muted text color so humans and bots line up
  const iconSx = { color: "text.secondary" };
  if (!isAiPlayer(player)) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 28 }}>
        <PersonOutlineIcon fontSize="small" sx={iconSx} />
        <Typography variant="body2" sx={{ flex: 1 }}>{player.name}</Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 28 }}>
      <SmartToyOutlinedIcon fontSize="small" sx={iconSx} />
      <Typography variant="body2" sx={{ flex: 1 }}>{player.name}</Typography>
      <BotStrengthChip
        level={aiDifficultyLevelFor(player.ai?.difficulty)}
        canManage={canManage}
        busy={busy}
        onChange={onSetDifficulty}
      />
      {canManage && (
        <Tooltip title="Remove bot">
          <span>
            <IconButton size="small" disabled={busy} onClick={onRemove} aria-label="Remove bot">
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

export default function MatchSummary() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, isSuperuser } = useAuth();
  const [matchDoc, setMatchDoc] = useState<Match | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!matchId) {
    throw "must have match id";
  }

  useEffect(() => {
    api.getMatch(matchId)
      .then((data) => {
        setMatchDoc(data.match);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [matchId]);

  // Subscribe to real-time match updates
  const handleMatchUpdate = useCallback((updatedMatch: Match) => {
    setMatchDoc(updatedMatch);
  }, []);

  useMatchSubscription(matchId, handleMatchUpdate);

  const { game, gameVersion } = useGameAndVersion(
    matchDoc?.gameId,
    null,
    matchDoc?.majorVersion,
    matchDoc?.minorVersionChannel
  );

  const isGameOwnerOrAdmin = !!(game && user && (game.ownerUserId === user.id || isSuperuser));
  const [debugMatch, setDebugMatch] = useState<Record<string, unknown> | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<MatchStatus>(matchDoc?.status ?? 'pending');
  const [selectedChannel, setSelectedChannel] = useState<VersionChannel>(matchDoc?.minorVersionChannel ?? 'stable');
  const [editedState, setEditedState] = useState<string>('');
  const [stateEditMode, setStateEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [botBusy, setBotBusy] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copying" | "copied" | "failed">("idle");

  /**
   * Copy the entire match document exactly as stored, state included, as pretty JSON. It is fetched on
   * click so the state is current rather than as of page load. The ClipboardItem form takes a promise,
   * which keeps Safari from rejecting a clipboard write that happens after a network round trip.
   */
  const copyFullMatch = async () => {
    if (!matchId) return;
    setCopyStatus("copying");
    const json = api.getMatchRaw(matchId).then((data) => JSON.stringify(data.match, null, 2));
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({ "text/plain": json.then((text) => new Blob([text], { type: "text/plain" })) }),
        ]);
      } else {
        await navigator.clipboard.writeText(await json);
      }
      setCopyStatus("copied");
    } catch (err) {
      console.error("Failed to copy match JSON:", err);
      setCopyStatus("failed");
    }
    setTimeout(() => setCopyStatus("idle"), 2000);
  };

  useEffect(() => {
    if (!isGameOwnerOrAdmin || !matchId) return;
    setDebugLoading(true);
    api.getMatchDebug(matchId)
      .then((data) => setDebugMatch(data.match as unknown as Record<string, unknown>))
      .catch(() => setDebugMatch(null))
      .finally(() => setDebugLoading(false));
  }, [isGameOwnerOrAdmin, matchId]);

  useEffect(() => {
    if (matchDoc) {
      setSelectedStatus(matchDoc.status);
      setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
    }
  }, [matchDoc?.status, matchDoc?.minorVersionChannel]);

  useEffect(() => {
    if (debugMatch?.state && !stateEditMode) {
      setEditedState(JSON.stringify(debugMatch.state, null, 2));
    }
  }, [debugMatch?.state, stateEditMode]);

  const hasControlChanges = selectedStatus !== matchDoc?.status ||
    selectedChannel !== (matchDoc?.minorVersionChannel ?? 'stable');

  const applyMatchChanges = async () => {
    if (!matchDoc) return;

    const updates: Record<string, unknown> = {};
    if (selectedStatus !== matchDoc.status) {
      updates.status = selectedStatus;
    }
    if (selectedChannel !== (matchDoc.minorVersionChannel ?? 'stable')) {
      updates.minorVersionChannel = selectedChannel;
    }
    if (Object.keys(updates).length === 0) return;

    setUpdating(true);
    try {
      const { match: updatedMatch } = await api.updateMatch(matchId, updates);
      setMatchDoc(updatedMatch);
      setSelectedStatus(updatedMatch.status);
      setSelectedChannel(updatedMatch.minorVersionChannel ?? 'stable');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update match");
      setSelectedStatus(matchDoc.status);
      setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
    } finally {
      setUpdating(false);
    }
  };

  const resetControlChanges = () => {
    if (!matchDoc) return;
    setSelectedStatus(matchDoc.status);
    setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
  };

  const saveMatchState = async () => {
    try {
      const parsedState = JSON.parse(editedState);
      if (typeof parsedState !== 'object' || parsedState === null || Array.isArray(parsedState)) {
        setError("State must be a JSON object, not an array or primitive");
        return;
      }
      setUpdating(true);
      const { match: updatedMatch } = await api.updateMatchStateAdmin(matchId, parsedState);
      setMatchDoc(updatedMatch);
      setDebugMatch({ ...debugMatch, state: parsedState } as Record<string, unknown>);
      setStateEditMode(false);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON: " + err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to update match state");
      }
    } finally {
      setUpdating(false);
    }
  };

  const cancelStateEdit = () => {
    setStateEditMode(false);
    if (debugMatch?.state) {
      setEditedState(JSON.stringify(debugMatch.state, null, 2));
    }
  };

  if (loading || !game || !gameVersion || !matchDoc) {
    return <PageTitle title="Loading...." />;
  }

  if (error) {
    return <PageTitle title={`Error: ${error}`} />;
  }

  const currentPlayerCount = Object.keys(matchDoc.players).length;
  const maxPlayerCount = matchDoc.numPlayers;
  const isPlaying = !!matchDoc.players[user?.id ?? "--"];

  const leaveGame = async () => {
    if (!user) {
      throw "must be logged in to leave the game";
    }

    setActionLoading(true);
    try {
      const { match: updatedMatch } = await api.leaveMatch(matchId);
      if (Object.keys(updatedMatch.players).length === 0) {
        navigate(`/game/${matchDoc.gameId}`);
      } else {
        setMatchDoc(updatedMatch);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to leave match");
    } finally {
      setActionLoading(false);
    }
  };

  const joinGame = async () => {
    if (!user) {
      throw "must be logged in to join the game";
    }

    setActionLoading(true);
    try {
      const { match: updatedMatch } = await api.joinMatch(matchId);
      setMatchDoc(updatedMatch);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to join match");
    } finally {
      setActionLoading(false);
    }
  };

  const statusColors: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
    pending: "warning",
    active: "primary",
    preparing: "primary",
    finished: "success",
    cancelled: "error",
    abandoned: "error",
  };

  const isDiscordMatch = !!matchDoc.discordThreadId;
  const canJoin = matchDoc.status === "pending" && !isPlaying && currentPlayerCount < maxPlayerCount && !isDiscordMatch;
  const canLeave = matchDoc.status === "pending" && isPlaying;

  // AI seats: anyone seated, the creator, or the game owner/admin may change them while the match is pending,
  // and only on builds whose engine can play them.
  const isCreator = !!user && matchDoc.createdByUserId === user.id;
  const canManageBots =
    matchDoc.status === "pending" && !!user && (isPlaying || isCreator || isGameOwnerOrAdmin) && !!gameVersion.supportsAi;
  const canAddBot = canManageBots && currentPlayerCount < maxPlayerCount;

  const runBotAction = async (action: () => Promise<{ match: Match }>) => {
    setBotBusy(true);
    setError(null);
    try {
      const { match: updatedMatch } = await action();
      setMatchDoc(updatedMatch);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update AI players");
    } finally {
      setBotBusy(false);
    }
  };

  return (
    <Box>
      <PageTitle title={matchDoc.name} />

      <Grid2 container spacing={2}>
        {/* Left column: the game, then who is playing */}
        <Grid2 size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            {/* Game Info Card */}
            <BoxCard title={game.name}>
              {game.boxArtUrl && (
                <Box
                  component="img"
                  src={game.boxArtUrl}
                  alt={game.name}
                  sx={{
                    width: "100%",
                    maxWidth: 200,
                    borderRadius: 1,
                    mb: 2,
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <InternalLink to={`/game/${game.id}`}>View Game Page</InternalLink>
              </Typography>
            </BoxCard>
            {/* Players Card (+ Bots card while seats are open) */}
            <BoxCard title={`Players (${currentPlayerCount}/${maxPlayerCount})`}>
              <Stack spacing={1}>
                {Object.keys(matchDoc.players).map((playerId) => (
                  <PlayerInfo
                    key={playerId}
                    player={matchDoc.players[playerId]}
                    canManage={canManageBots}
                    busy={botBusy}
                    onSetDifficulty={(level) => runBotAction(() => api.setAiSeatDifficulty(matchId, playerId, level))}
                    onRemove={() => runBotAction(() => api.removeAiSeat(matchId, playerId))}
                  />
                ))}
                {currentPlayerCount === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No players yet
                  </Typography>
                )}
                {canJoin && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={actionLoading}
                    onClick={() => {
                      if (!user) {
                        window.localStorage.setItem("postLoginUrl", document.location.href);
                        navigate("/login");
                        return;
                      }
                      joinGame();
                    }}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                      fontWeight: 600,
                      mt: 1,
                      alignSelf: "flex-start",
                    }}
                  >
                    {actionLoading ? "Joining..." : "Join"}
                  </Button>
                )}
                {isDiscordMatch && matchDoc.status === "pending" && !isPlaying && currentPlayerCount < maxPlayerCount && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This match was created via Discord.{" "}
                    {matchDoc.discordGuildId ? (
                      <Link
                        href={`https://discord.com/channels/${matchDoc.discordGuildId}/${matchDoc.discordThreadId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join in Discord
                      </Link>
                    ) : (
                      "Join via the Discord thread."
                    )}
                  </Typography>
                )}
                {canLeave && (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={actionLoading}
                    onClick={leaveGame}
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.text.primary,
                      mt: 1,
                      alignSelf: "flex-start",
                      "&:hover": {
                        borderColor: theme.palette.primary.light,
                        background: "rgba(255, 63, 127, 0.1)",
                      },
                    }}
                  >
                    {actionLoading ? "Leaving..." : "Leave"}
                  </Button>
                )}
              </Stack>
            </BoxCard>

            {canAddBot && (
              <BoxCard title="Add a bot">
                <Typography variant="body2" color="text.secondary">
                  {maxPlayerCount - currentPlayerCount === 1
                    ? "Fill the last seat with a computer player."
                    : `Fill one of ${maxPlayerCount - currentPlayerCount} open seats with a computer player.`}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {AI_DIFFICULTY_LEVELS.map((level) => (
                    <Button
                      key={level}
                      variant="outlined"
                      disabled={botBusy}
                      onClick={() => runBotAction(() => api.addAiSeat(matchId, level))}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        py: 1,
                        flexDirection: "column",
                        textTransform: "none",
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.text.primary,
                        "&:hover": {
                          borderColor: theme.palette.primary.light,
                          background: "rgba(255, 63, 127, 0.1)",
                        },
                      }}
                    >
                      <Box sx={{ fontWeight: 600, lineHeight: 1.3 }}>{AI_DIFFICULTY_LABEL[level]}</Box>
                      <Box sx={{ fontSize: "0.75rem", lineHeight: 1.3, color: "text.secondary" }}>
                        {AI_DIFFICULTY_BLURB[level]}
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </BoxCard>
            )}
          </Stack>
        </Grid2>

        {/* Match Info Card */}
        <Grid2 size={{ xs: 12, md: 7 }}>
          <BoxCard title="Match Info">
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "grid",
                  // Labels take what they need up to 40% and wrap past that; values get the rest
                  gridTemplateColumns: "fit-content(40%) minmax(0, 1fr)",
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: "baseline",
                }}
              >
                <InfoLabel>Status</InfoLabel>
                <Box>
                  <Chip
                    label={matchDoc.status}
                    size="small"
                    color={statusColors[matchDoc.status] || "default"}
                  />
                </Box>
                <InfoLabel>Created by</InfoLabel>
                <InfoValue>{matchDoc.createdByName}</InfoValue>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>
                  {new Date(matchDoc.createdAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </InfoValue>
                {matchDoc.options && gameVersion.configurationOptions?.map((opt) => {
                  const selected = matchDoc.options?.[opt.fieldName];
                  if (selected === undefined) return null;
                  const labelFor = (id: string) => opt.config.choices.find(c => c.id === id)?.label ?? id;
                  // Multi-select values follow the game's choice order, one chip each
                  const value = Array.isArray(selected) ? (
                    selected.length ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {opt.config.choices
                          .filter((c) => selected.includes(c.id))
                          .map((c) => (
                            <Chip key={c.id} label={c.label} size="small" variant="outlined" />
                          ))}
                      </Box>
                    ) : "None"
                  ) : labelFor(selected);
                  return (
                    <Fragment key={opt.fieldName}>
                      <InfoLabel>{opt.config.label}</InfoLabel>
                      <InfoValue>{value}</InfoValue>
                    </Fragment>
                  );
                })}
              </Box>
              {matchDoc.status === "active" && (
                <Button
                  variant="contained"
                  size="small"
                  component={Link}
                  href={`/player/${matchDoc.id}`}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                    fontWeight: 600,
                    mt: 1,
                    alignSelf: "flex-start",
                  }}
                >
                  Launch Player
                </Button>
              )}
              {matchDoc.discordThreadId && matchDoc.discordGuildId && (
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  href={`https://discord.com/channels/${matchDoc.discordGuildId}/${matchDoc.discordThreadId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    mt: 1,
                    alignSelf: "flex-start",
                    "&:hover": {
                      borderColor: theme.palette.primary.light,
                      background: "rgba(255, 63, 127, 0.1)",
                    },
                  }}
                >
                  Discord Thread
                </Button>
              )}
            </Stack>
          </BoxCard>
        </Grid2>

      </Grid2>

      {isGameOwnerOrAdmin && (
        <BoxCard title="Match Controls (Admin)" sx={{ mt: 2 }}>
          <Stack spacing={2}>
            {/* Status Control */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Match status:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  disabled={updating}
                  onChange={(e) => setSelectedStatus(e.target.value as MatchStatus)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="preparing">Preparing</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="finished">Finished</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="abandoned">Abandoned</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Version Channel Control */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Minor version channel:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Channel</InputLabel>
                <Select
                  value={selectedChannel}
                  label="Channel"
                  disabled={updating}
                  onChange={(e) => setSelectedChannel(e.target.value as VersionChannel)}
                >
                  <MenuItem value="stable">Stable</MenuItem>
                  <MenuItem value="beta">Beta</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {hasControlChanges && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={updating}
                  onClick={applyMatchChanges}
                >
                  {updating ? "Applying..." : "Apply Changes"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={updating}
                  onClick={resetControlChanges}
                >
                  Reset
                </Button>
              </Stack>
            )}

            {/* State Editor */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Edit match state (JSON):
              </Typography>
              <TextField
                multiline
                fullWidth
                value={editedState}
                onChange={(e) => setEditedState(e.target.value)}
                slotProps={{
                  input: {
                    readOnly: !stateEditMode,
                    sx: { fontFamily: "monospace", fontSize: 12 }
                  }
                }}
                minRows={6}
                maxRows={20}
                error={stateEditMode && (() => {
                  try { JSON.parse(editedState); return false; } catch { return true; }
                })()}
                helperText={stateEditMode && (() => {
                  try { JSON.parse(editedState); return "Valid JSON"; } catch (e) { return `Invalid JSON: ${(e as Error).message}`; }
                })()}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {!stateEditMode ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setStateEditMode(true)}
                    disabled={!debugMatch?.state}
                  >
                    Edit State
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveMatchState}
                      disabled={updating || (() => { try { JSON.parse(editedState); return false; } catch { return true; } })()}
                    >
                      Save State
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={cancelStateEdit}
                      disabled={updating}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </BoxCard>
      )}

      {isGameOwnerOrAdmin && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <BoxCard title="Match Metadata (Debug)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              disabled={copyStatus === "copying"}
              onClick={copyFullMatch}
              sx={{
                alignSelf: "flex-start",
                borderColor: "rgba(255, 255, 255, 0.3)",
                color: theme.palette.text.primary,
                "&:hover": { borderColor: theme.palette.primary.light, background: "rgba(255, 255, 255, 0.06)" },
              }}
            >
              {{
                idle: "Copy full match JSON",
                copying: "Copying...",
                copied: "Copied to clipboard",
                failed: "Copy failed",
              }[copyStatus]}
            </Button>
            {debugLoading ? (
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            ) : debugMatch ? (
              <TextField
                multiline
                fullWidth
                value={JSON.stringify(Object.fromEntries(Object.entries(debugMatch).filter(([k]) => k !== "state")), null, 2)}
                slotProps={{ input: { readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } } }}
                minRows={6}
                maxRows={20}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Unable to load match data.</Typography>
            )}
          </BoxCard>
        </Stack>
      )}
    </Box>
  );
}
